// src/components/StaffManager.js
//
// Settings -> Staff. Admins and managers can add a staff member, change role,
// name, salary, monthly target, rota status and login, reset a password, and
// mark someone as resigned.
//
// Staff are never deleted: a user is attached to every service they performed
// and every bill they raised, so removing one would take that history with it.
// Resigning records the leaving date, takes them off the rota and closes their
// login. They move to the Resigned Staff tab and can be reinstated from there.
//
// The name shown everywhere in the app is First + Last, falling back to the
// username. Two staff who would read identically get the login handle appended
// by the server, so "Sara Ahmed" and "Sara Ahmed (sara2)" can be told apart.

import React, { useEffect, useMemo, useState } from "react";
import { fetchWithAuth } from "../config/api";

const BLANK_NEW = {
  username: "",
  password: "",
  first_name: "",
  last_name: "",
  role: "staff",
  salary: "",
  monthly_target: "",
};

function todayISO() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function StaffManager() {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({});
  const [canManageAdmins, setCanManageAdmins] = useState(false);
  const [me, setMe] = useState(null);

  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [showAdd, setShowAdd] = useState(false);
  const [newStaff, setNewStaff] = useState(BLANK_NEW);
  const [adding, setAdding] = useState(false);

  const [pwFor, setPwFor] = useState(null);
  const [pwValue, setPwValue] = useState("");

  const [resignFor, setResignFor] = useState(null);
  const [resignDate, setResignDate] = useState(todayISO());

  async function load() {
    setLoading(true);
    setError("");
    try {
      const res = await fetchWithAuth("/api/manage/staff/");
      const js = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(js.error || "Could not load staff.");
        return;
      }
      setRows(js.staff || []);
      setCanManageAdmins(Boolean(js.can_manage_admins));
      setMe(js.me ?? null);
      setDraft({});
    } catch {
      setError("Could not reach the server.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // This tab is the current team; people who have left live in Resigned Staff.
  const active = useMemo(() => rows.filter((r) => !r.resigned_on), [rows]);

  function edit(id, field, value) {
    setError("");
    setNotice("");
    setDraft((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  }

  function valueOf(row, field) {
    const edited = draft[row.id];
    return edited && field in edited ? edited[field] : row[field];
  }

  const changedIds = useMemo(
    () =>
      new Set(
        Object.entries(draft)
          .filter(([id, fields]) => {
            const row = rows.find((r) => String(r.id) === String(id));
            if (!row) return false;
            return Object.entries(fields).some(([k, v]) => String(v) !== String(row[k]));
          })
          .map(([id]) => Number(id))
      ),
    [draft, rows]
  );

  function lockedReason(row) {
    if (!canManageAdmins && (row.role === "admin" || row.is_superuser)) {
      return "Only an admin can change an admin account.";
    }
    return "";
  }

  async function patchStaff(row, payload, successText) {
    setSavingId(row.id);
    setError("");
    setNotice("");
    try {
      const res = await fetchWithAuth(`/api/manage/staff/${row.id}/`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      const js = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(js.error || "Could not save.");
        return null;
      }
      setRows((prev) => prev.map((r) => (r.id === row.id ? js : r)));
      setDraft((prev) => {
        const copy = { ...prev };
        delete copy[row.id];
        return copy;
      });
      if (successText) setNotice(successText(js));
      return js;
    } finally {
      setSavingId(null);
    }
  }

  async function saveRow(row) {
    const fields = draft[row.id] || {};
    const payload = {};
    [
      "role", "salary", "monthly_target", "is_active_staff",
      "can_login", "first_name", "last_name",
    ].forEach((k) => {
      if (k in fields && String(fields[k]) !== String(row[k])) payload[k] = fields[k];
    });
    if (!Object.keys(payload).length) return;
    await patchStaff(row, payload, (js) => `Saved ${js.display_name}.`);
  }

  async function addStaff() {
    setError("");
    setNotice("");
    if (!newStaff.username.trim()) {
      setError("A username is required - this is what they sign in with.");
      return;
    }
    if (newStaff.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setAdding(true);
    try {
      const res = await fetchWithAuth("/api/manage/staff/", {
        method: "POST",
        body: JSON.stringify({
          ...newStaff,
          salary: Number(newStaff.salary || 0),
          monthly_target: Number(newStaff.monthly_target || 0),
        }),
      });
      const js = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(js.error || "Could not add the staff member.");
        return;
      }
      setRows((prev) => [...prev, js].sort((a, b) => a.username.localeCompare(b.username)));
      setNewStaff(BLANK_NEW);
      setShowAdd(false);
      setNotice(`${js.display_name} added as ${js.role}.`);
    } finally {
      setAdding(false);
    }
  }

  async function resetPassword(row) {
    if (pwValue.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    const js = await patchStaff(row, { password: pwValue }, null);
    if (js) {
      setPwFor(null);
      setPwValue("");
      setNotice(`Password updated for ${js.display_name}.`);
    }
  }

  async function confirmResign(row) {
    const js = await patchStaff(row, { resigned_on: resignDate }, null);
    if (js) {
      setResignFor(null);
      setNotice(
        `${js.display_name} marked as resigned on ${resignDate}. ` +
          "They are off the rota, their login is closed, and their past work is unchanged. " +
          "See the Resigned Staff tab."
      );
    }
  }

  const cell = { padding: "6px 8px", verticalAlign: "middle" };
  const tiny = { fontSize: 11, color: "#777" };

  return (
    <div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <h4 style={{ margin: 0 }}>Staff</h4>
        <button className="btn" type="button" onClick={() => setShowAdd((v) => !v)}>
          {showAdd ? "Cancel" : "+ Add staff member"}
        </button>
        <button className="btn" type="button" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
        <span style={{ color: "#666", fontSize: 13 }}>{active.length} on the team</span>
      </div>

      {error && (
        <div style={{ background: "#fdecea", border: "1px solid #f5a29b", color: "#8a1c12",
                      borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
          {error}
        </div>
      )}
      {notice && (
        <div style={{ background: "#e8f5e9", border: "1px solid #8ccf91", color: "#1b5e20",
                      borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
          {notice}
        </div>
      )}

      {showAdd && (
        <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, marginBottom: 14 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ display: "grid", gap: 4 }}>
              <span style={{ fontSize: 12 }}>First name</span>
              <input className="input" style={{ maxWidth: 140 }} value={newStaff.first_name}
                     onChange={(e) => setNewStaff({ ...newStaff, first_name: e.target.value })} />
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              <span style={{ fontSize: 12 }}>Last name</span>
              <input className="input" style={{ maxWidth: 140 }} value={newStaff.last_name}
                     onChange={(e) => setNewStaff({ ...newStaff, last_name: e.target.value })} />
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              <span style={{ fontSize: 12 }}>Username (for signing in)</span>
              <input className="input" style={{ maxWidth: 160 }} value={newStaff.username}
                     onChange={(e) => setNewStaff({ ...newStaff, username: e.target.value })} />
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              <span style={{ fontSize: 12 }}>Password (min 8)</span>
              <input className="input" type="password" style={{ maxWidth: 150 }} value={newStaff.password}
                     onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })} />
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              <span style={{ fontSize: 12 }}>Role</span>
              <select className="input" value={newStaff.role}
                      onChange={(e) => setNewStaff({ ...newStaff, role: e.target.value })}>
                <option value="staff">Staff</option>
                <option value="manager">Manager</option>
                {canManageAdmins && <option value="admin">Admin</option>}
              </select>
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              <span style={{ fontSize: 12 }}>Salary</span>
              <input className="input" type="number" min="0" step="0.01" style={{ maxWidth: 110 }}
                     value={newStaff.salary}
                     onChange={(e) => setNewStaff({ ...newStaff, salary: e.target.value })} />
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              <span style={{ fontSize: 12 }}>Monthly target</span>
              <input className="input" type="number" min="0" step="0.01" style={{ maxWidth: 130 }}
                     value={newStaff.monthly_target}
                     onChange={(e) => setNewStaff({ ...newStaff, monthly_target: e.target.value })} />
            </label>
            <button className="btn" type="button" onClick={addStaff} disabled={adding}>
              {adding ? "Adding..." : "Add"}
            </button>
          </div>
          <div style={{ ...tiny, marginTop: 8 }}>
            The username is the login handle and must be unique. First and last name are what
            everyone sees when picking staff, so fill them in when two people share a first name.
            {!canManageAdmins && " Only an admin can create another admin."}
          </div>
        </div>
      )}

      {loading && <div>Loading staff...</div>}

      {!loading && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 940 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #eee", textAlign: "left" }}>
                <th style={cell}>Name</th>
                <th style={cell}>Role</th>
                <th style={cell}>Salary</th>
                <th style={cell}>Monthly target</th>
                <th style={cell}>In rota</th>
                <th style={cell}>Can log in</th>
                <th style={cell}></th>
              </tr>
            </thead>
            <tbody>
              {active.map((row) => {
                const locked = lockedReason(row);
                const isMe = row.id === me;
                const dirty = changedIds.has(row.id);
                return (
                  <React.Fragment key={row.id}>
                    <tr style={{ borderBottom: "1px solid #f3f3f3", opacity: row.can_login ? 1 : 0.6 }}>
                      <td style={cell}>
                        <div>
                          <strong>{row.display_name}</strong>
                          {isMe && <span style={tiny}> (you)</span>}
                        </div>
                        <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                          <input className="input" placeholder="First" style={{ maxWidth: 95 }}
                                 disabled={Boolean(locked)} value={valueOf(row, "first_name") || ""}
                                 onChange={(e) => edit(row.id, "first_name", e.target.value)} />
                          <input className="input" placeholder="Last" style={{ maxWidth: 95 }}
                                 disabled={Boolean(locked)} value={valueOf(row, "last_name") || ""}
                                 onChange={(e) => edit(row.id, "last_name", e.target.value)} />
                        </div>
                        <div style={tiny}>signs in as {row.username}</div>
                      </td>
                      <td style={cell}>
                        <select className="input" disabled={Boolean(locked) || isMe}
                                title={isMe ? "You cannot change your own role." : locked}
                                value={valueOf(row, "role")}
                                onChange={(e) => edit(row.id, "role", e.target.value)}>
                          <option value="staff">Staff</option>
                          <option value="manager">Manager</option>
                          {(canManageAdmins || row.role === "admin") && (
                            <option value="admin">Admin</option>
                          )}
                        </select>
                      </td>
                      <td style={cell}>
                        <input className="input" type="number" min="0" step="0.01"
                               style={{ maxWidth: 105 }} disabled={Boolean(locked)}
                               value={valueOf(row, "salary")}
                               onChange={(e) => edit(row.id, "salary", e.target.value)} />
                      </td>
                      <td style={cell}>
                        <input className="input" type="number" min="0" step="0.01"
                               style={{ maxWidth: 115 }} disabled={Boolean(locked)}
                               value={valueOf(row, "monthly_target")}
                               onChange={(e) => edit(row.id, "monthly_target", e.target.value)} />
                      </td>
                      <td style={cell}>
                        <input type="checkbox" disabled={Boolean(locked)}
                               checked={Boolean(valueOf(row, "is_active_staff"))}
                               onChange={(e) => edit(row.id, "is_active_staff", e.target.checked)} />
                      </td>
                      <td style={cell}>
                        <input type="checkbox" disabled={Boolean(locked) || isMe}
                               title={isMe ? "You cannot disable your own login." : locked}
                               checked={Boolean(valueOf(row, "can_login"))}
                               onChange={(e) => edit(row.id, "can_login", e.target.checked)} />
                      </td>
                      <td style={{ ...cell, whiteSpace: "nowrap" }}>
                        <button className="btn" type="button" onClick={() => saveRow(row)}
                                disabled={!dirty || savingId === row.id || Boolean(locked)}>
                          {savingId === row.id ? "Saving..." : "Save"}
                        </button>{" "}
                        <button className="btn" type="button" disabled={Boolean(locked)}
                                onClick={() => { setPwFor(pwFor === row.id ? null : row.id); setPwValue(""); }}>
                          Password
                        </button>{" "}
                        <button className="btn" type="button"
                                disabled={Boolean(locked) || isMe}
                                title={isMe ? "You cannot mark your own account as resigned." : locked}
                                onClick={() => {
                                  setResignFor(resignFor === row.id ? null : row.id);
                                  setResignDate(todayISO());
                                }}>
                          Resign
                        </button>
                      </td>
                    </tr>

                    {locked && (
                      <tr>
                        <td colSpan={7} style={{ ...cell, paddingTop: 0, fontSize: 12, color: "#8a4b00" }}>
                          {locked}
                        </td>
                      </tr>
                    )}

                    {pwFor === row.id && (
                      <tr>
                        <td colSpan={7} style={{ ...cell, background: "#fafafa" }}>
                          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <span style={{ fontSize: 13 }}>
                              New password for <strong>{row.display_name}</strong>:
                            </span>
                            <input className="input" type="password" value={pwValue}
                                   style={{ maxWidth: 200 }}
                                   onChange={(e) => setPwValue(e.target.value)} />
                            <button className="btn" type="button" onClick={() => resetPassword(row)}
                                    disabled={savingId === row.id}>
                              Set password
                            </button>
                            <button className="btn" type="button" onClick={() => setPwFor(null)}>
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}

                    {resignFor === row.id && (
                      <tr>
                        <td colSpan={7} style={{ ...cell, background: "#fff8f0" }}>
                          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <span style={{ fontSize: 13 }}>
                              Last working day for <strong>{row.display_name}</strong>:
                            </span>
                            <input className="input" type="date" max={todayISO()} value={resignDate}
                                   onChange={(e) => setResignDate(e.target.value)} />
                            <button className="btn" type="button" onClick={() => confirmResign(row)}
                                    disabled={savingId === row.id}>
                              Mark as resigned
                            </button>
                            <button className="btn" type="button" onClick={() => setResignFor(null)}>
                              Cancel
                            </button>
                          </div>
                          <div style={{ ...tiny, marginTop: 6 }}>
                            They come off the rota and their login is closed. Nothing they have
                            already done is changed - past bills and Staff Performance keep their
                            work. They move to the Resigned Staff tab and can be brought back.
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ fontSize: 12, color: "#666", marginTop: 12, lineHeight: 1.6 }}>
        <div><strong>In rota</strong> - shows and works in billing, booking and staff
          assignment. Turn this off for someone who is away but still employed.</div>
        <div><strong>Can log in</strong> - whether they can sign in to the system, nothing more.
          It does <em>not</em> affect their work: they still appear when billing, booking and
          assigning services, and their performance is still recorded.</div>
        <div><strong>Resign</strong> - records a leaving date and moves them to Resigned Staff.
          This is the only switch that removes someone from the day-to-day screens.</div>
      </div>
    </div>
  );
}
